"use client";

import { forwardRef, type ComponentProps } from "react";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";

import { cn } from "@/lib/utils";
import { type ButtonProps, buttonVariants } from "./Button";

const PaginationNav = ({ className, ...props }: ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
);
PaginationNav.displayName = "PaginationNav";

const PaginationContent = forwardRef<HTMLUListElement, ComponentProps<"ul">>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn("flex flex-row items-center gap-2", className)}
      {...props}
    />
  )
);
PaginationContent.displayName = "PaginationContent";

const PaginationItem = forwardRef<HTMLLIElement, ComponentProps<"li">>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn("", className)} {...props} />
  )
);
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, "size"> &
  ComponentProps<"a">;

const PaginationLink = ({
  className,
  isActive,
  size = "icon",
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? "outline" : "ghost",
        size,
      }),
      className
    )}
    {...props}
  />
);
PaginationLink.displayName = "PaginationLink";

const PaginationPrevious = ({
  className,
  ...props
}: ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Перейти на предыдущую страницу"
    size="default"
    className={cn("gap-2 pl-2", className)}
    {...props}
  >
    <ChevronLeft className="h-4 w-4" />
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({
  className,
  ...props
}: ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Перейти на следующую страницу"
    size="default"
    className={cn("gap-2 pr-2", className)}
    {...props}
  >
    <ChevronRight className="h-4 w-4" />
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({
  className,
  ...props
}: ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

interface PaginationProps {
  total: number;
  current: number;
}

export function Pagination({ total, current }: PaginationProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  if (total < 2) return null;
  const makeLink = (page: number) => {
    const currentParams = new URLSearchParams(searchParams.toString());
    currentParams.set("page", String(page));
    return `${pathname}?${currentParams.toString()}`;
  };

  return (
    <PaginationNav className="mt-20">
      <PaginationContent>
        {current > 1 && (
          <PaginationItem>
            <PaginationPrevious href={makeLink(current - 1)} />
          </PaginationItem>
        )}
        {current > 2 && (
          <PaginationItem>
            <PaginationLink href={makeLink(1)}>1</PaginationLink>
          </PaginationItem>
        )}
        {current > 3 && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}
        {current > 1 && (
          <PaginationItem>
            <PaginationLink href={makeLink(current - 1)}>
              {current - 1}
            </PaginationLink>
          </PaginationItem>
        )}
        <PaginationItem>
          <PaginationLink href="#" isActive>
            {current}
          </PaginationLink>
        </PaginationItem>
        {total > current && (
          <PaginationItem>
            <PaginationLink href={makeLink(current + 1)}>
              {current + 1}
            </PaginationLink>
          </PaginationItem>
        )}
        {total > 3 && current < total - 2 && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}
        {total > current + 1 && (
          <PaginationItem>
            <PaginationLink href={makeLink(total)}>{total}</PaginationLink>
          </PaginationItem>
        )}
        {current < total && (
          <PaginationItem>
            <PaginationNext href={makeLink(current + 1)} />
          </PaginationItem>
        )}
      </PaginationContent>
    </PaginationNav>
  );
}
